import { Link } from "react-router-dom";
import { Stamp } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";
import { useBonusVenues } from "@/hooks/useBonusVenues";
import { useShowcase } from "@/services/showcase";
import { bonusItemName, formatBonusDate, formatRon } from "@/lib/bonusFormat";

/**
 * "Resto Bonus venue" on the venue detail (docs/features/bonus.md Rule 7): the
 * item and its condition are visible before anyone collects, so guests see it
 * too. An opted-out venue that still honours earlier bonuses says until when.
 */
export function BonusVenueChip({ venueId }: { venueId: string }) {
  const { t, lang } = useTranslation();
  // The /showcase-cluj demo makes no server calls (showcase.md Rule 2): no chip there.
  const venues = useBonusVenues(!useShowcase());
  const v = venues?.find((row) => row.venue_id === venueId);
  if (!v) return null;

  const item = bonusItemName(v.item, lang);
  return (
    <div className="flex gap-3 rounded-xl border border-pulse/25 bg-pulse/[0.06] p-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-pulse/15 text-pulse">
        <Stamp className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 space-y-1">
        <p className="font-mono text-3xs uppercase tracking-[0.18em] text-dim light:text-zinc-600">
          {t("BONUS_CHIP_TITLE")}
        </p>
        <p className="text-sm font-medium text-zinc-100 light:text-zinc-900">
          {t("BONUS_CONDITION", { item })}
          {v.item.min_order_ron != null && (
            <span className="font-normal text-dim light:text-zinc-600">
              {" · "}
              {t("BONUS_MIN_ORDER", { ron: formatRon(v.item.min_order_ron) })}
            </span>
          )}
        </p>
        <p className="text-xs text-dim light:text-zinc-600">
          {v.enabled || !v.honours_until
            ? t("BONUS_CHIP_HINT")
            : t("BONUS_CHIP_HONOURING", { date: formatBonusDate(v.honours_until, lang) })}
        </p>
        {v.next_item && v.next_effective_at && (
          <p className="text-xs text-dim light:text-zinc-600">
            {t("BONUS_NEXT_ITEM", {
              date: formatBonusDate(v.next_effective_at, lang),
              item: bonusItemName(v.next_item, lang),
            })}
          </p>
        )}
        <Link
          to="/bonus-rules"
          className="inline-block text-xs text-zinc-300 light:text-zinc-700 underline decoration-white/30 light:decoration-black/30 underline-offset-2 hover:text-zinc-50 light:hover:text-zinc-900"
        >
          {t("BONUS_RULES_LINK")}
        </Link>
      </div>
    </div>
  );
}
